'use client'

import { type KeyboardEvent, type MouseEvent, type ReactNode, useEffect, useMemo, useRef, useState } from 'react'

import { CLUSTER_CONFIG, type ClusterKey, getMarketGetAction, getMarketVaultSortRank } from '@/config/market-config'
import { useAccount } from '@/domain/hooks/useAccount'
import { getTokenImageUrl } from '@/domain/euler'
import { useOpenDialog } from '@/domain/hooks/useDialog'
import { getDisplayTokenSymbol } from '@/domain/hooks/useDisplayToken'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { USD_MOCK_TOKEN } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { depositDialogConfig as pooledDepositDialogConfig } from '@/features/dialogs/deposit/DepositDialog'
import {
  type ELendVaultExposureSummary,
  useELendVaultExposureSummaries,
} from '@/features/elend-details/logic/useELendVaultExposureSummaries'
import { hasAnyFraxlendPause } from '@/features/isolated/types/FraxlendPairPauseState'
import {
  type HoldingsData,
  getPortfolioHoldingsData,
  getPortfolioHoldingsSortValue,
  usePortfolioEvmBalances,
} from '@/features/portfolio'
import { HoldingsCellBase } from '@/features/unified-earn/components/HoldingsCell'
import type { YieldOpportunity } from '@/features/unified-earn/types'
import { cleanVaultName } from '@/features/unified-earn/utils/cleanVaultName'
import { getMarketType } from '@/features/unified-earn/utils/getMarketType'
import { getYieldOpportunityDetailsPath } from '@/features/unified-earn/utils/getYieldOpportunityDetailsPath'
import { Button } from '@/ui/atoms/button/Button'
import { Dash } from '@/ui/atoms/dash/Dash'
import { TokenImage } from '@/ui/atoms/token-image/TokenImage'
import { FraxlendPauseIndicator } from '@/ui/molecules/fraxlend-pause-indicator/FraxlendPauseIndicator'
import { FrozenPill } from '@/ui/molecules/frozen-pill/FrozenPill'
import { Info } from '@/ui/molecules/info/Info'
import { SectionHeader } from '@/ui/molecules/section-header/SectionHeader'
import { SupplyApyTooltip } from '@/ui/molecules/supply-apy-tooltip/SupplyApyTooltip'
import { cn } from '@/ui/utils/style'
import { ArrowRight, ChevronDown, ChevronUp, Sparkles } from 'lucide-react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { marketDepositActionDialogConfig } from './MarketDepositActionDialog'
import { marketGetActionDialogConfig } from './MarketGetActionDialog'

function formatUsdCompact(value: number | undefined): string {
  if (!value || value === 0) return '-'
  return USD_MOCK_TOKEN.formatUSD(NormalizedUnitNumber(value), { compact: true })
}

function formatApy(value: number | null | undefined): string {
  if (value == null || value === 0) return '-'
  if (value > 0 && value < 0.005) return '<0.01%'
  return `${value.toFixed(2)}%`
}

function ValueText({ value }: { value: string }) {
  if (value === '-') return <Dash />
  return <span className="font-medium text-foreground text-sm">{value}</span>
}

function getClusterExposureSummary(cluster: ProductCluster): { assets: string; pairs: string } {
  const assetAddresses = new Set<string>()
  let pairs = 0
  let hasPooledExposure = false

  for (const opportunity of cluster.opportunities) {
    const exposure = opportunity.exposure
    if (opportunity.token?.address) {
      assetAddresses.add(opportunity.token.address.toLowerCase())
    }
    if (!exposure) continue

    if (opportunity.source === 'pooled' || exposure.type === 'shared-pool') {
      hasPooledExposure = true
      continue
    }

    if (exposure.type === 'isolated-pair' && exposure.isolatedPair) {
      assetAddresses.add(exposure.isolatedPair.collateralToken.address.toLowerCase())
      pairs += 1
      continue
    }

    if (exposure.type === 'vault-collaterals' && exposure.collaterals) {
      for (const collateral of exposure.collaterals) {
        if (collateral.assetAddress) {
          assetAddresses.add(collateral.assetAddress.toLowerCase())
        }
        pairs += 1
      }
      continue
    }

    if (exposure.type === 'vault-strategies' && exposure.strategies) {
      for (const strategy of exposure.strategies) {
        if (strategy.assetAddress) {
          assetAddresses.add(strategy.assetAddress.toLowerCase())
        }
        pairs += 1
      }
    }
  }

  if (hasPooledExposure) {
    return {
      assets: `${assetAddresses.size} assets`,
      pairs: 'Shared Pool',
    }
  }

  return {
    assets: `${assetAddresses.size} assets`,
    pairs: `${pairs} pairs`,
  }
}

export interface CuratorInfo {
  name: string
  logo: string
}

export interface ProductCluster {
  key: ClusterKey
  name: string
  description: string
  opportunities: YieldOpportunity[]
  highestApy: number
  highestApy30d: number
  totalTVL: number
  curators: CuratorInfo[]
}

interface ProductsTableProps {
  clusters: ProductCluster[]
  selectedCluster: ClusterKey | null
  scrollToCluster?: ClusterKey | null
  onClusterClick: (key: ClusterKey) => void
  onClusterScrolled?: () => void
}

const CLUSTER_HEADERS: ReadonlyArray<{ label: string; tooltip?: string; colSpan?: number }> = [
  { label: 'Market' },
  {
    label: 'APY',
    tooltip:
      'The best current total supply APY across all vaults in this market, including lending APY and any intrinsic asset APY.',
  },
  {
    label: '30d APY',
    tooltip:
      'The best 30-day average lending APY across all vaults in this market. Intrinsic asset APY is not tracked in this average.',
  },
  { label: 'TVL', tooltip: 'Total value locked across all vaults in this market.' },
  { label: 'Assets', tooltip: 'Number of unique assets and lending pairs in this market.' },
  { label: '' },
]

export function ProductsTable({
  clusters,
  selectedCluster,
  scrollToCluster,
  onClusterClick,
  onClusterScrolled,
}: ProductsTableProps) {
  const clusterRefs = useRef(new Map<ClusterKey, HTMLDivElement>())
  const elendVaultAddresses = useMemo(
    () =>
      clusters.flatMap((cluster) =>
        cluster.opportunities
          .filter((opportunity) => opportunity.source === 'elend')
          .map((opportunity) => opportunity.address),
      ),
    [clusters],
  )
  const elendExposureSummaries = useELendVaultExposureSummaries(elendVaultAddresses)

  useEffect(() => {
    if (!scrollToCluster || selectedCluster !== scrollToCluster) return

    const frameId = requestAnimationFrame(() => {
      clusterRefs.current.get(scrollToCluster)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      onClusterScrolled?.()
    })

    return () => cancelAnimationFrame(frameId)
  }, [onClusterScrolled, scrollToCluster, selectedCluster])

  return (
    <div className="flex flex-col gap-3">
      <div className="mb-6">
        <SectionHeader
          icon={<Sparkles className="h-4 w-4 text-[#FBE572]" />}
          title="Lending Markets"
          description="Overcollateralized lending markets with transparent rates and collateral requirements. Select a market to evaluate parameters and deposit."
        />
      </div>

      <div className={cn('hidden items-center px-5 md:grid', DETAIL_COL_GRID)}>
        {CLUSTER_HEADERS.map(({ label, tooltip, colSpan }, index) => (
          <span
            key={label || `col-${index}`}
            className={cn('flex items-center gap-1 text-muted-foreground text-xs', index > 0 && 'justify-end')}
            style={colSpan ? { gridColumn: `span ${colSpan}` } : undefined}
          >
            {label} {tooltip && <Info size={12}>{tooltip}</Info>}
          </span>
        ))}
      </div>

      {clusters.map((cluster) => {
        const style = CLUSTER_CONFIG[cluster.key]
        if (!style) return null
        const Icon = style.icon
        const isSelected = selectedCluster === cluster.key
        const exposureSummary = getClusterExposureSummary(cluster)

        return (
          <div
            key={cluster.key}
            ref={(node) => {
              if (node) {
                clusterRefs.current.set(cluster.key, node)
              } else {
                clusterRefs.current.delete(cluster.key)
              }
            }}
            className="flex scroll-mt-20 flex-col gap-3"
          >
            <button
              type="button"
              onClick={() => onClusterClick(cluster.key)}
              className={cn(
                'group w-full cursor-pointer rounded-lg border text-left shadow-sm backdrop-blur-sm transition-all',
                isSelected ? 'border-border bg-panel' : 'border-border bg-panel hover:bg-panel/80',
              )}
            >
              <div className="p-4 sm:p-5">
                <div className={cn('items-center md:grid', DETAIL_COL_GRID)}>
                  <div className="flex min-w-0 items-center gap-4">
                    <div
                      className={cn(
                        'flex h-11 w-11 shrink-0 items-center justify-center rounded-lg',
                        style.background,
                        style.foreground,
                      )}
                    >
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="truncate bg-gradient-to-r from-gradient-yellow to-gradient-green bg-clip-text font-bold text-[15px] text-transparent">
                          {cluster.name}
                        </span>
                        <ChevronDown
                          className={cn(
                            'h-4 w-4 transition-transform duration-200',
                            isSelected ? 'rotate-180 text-foreground' : 'text-muted-foreground/40',
                          )}
                        />
                      </div>
                      <span className="truncate text-muted-foreground text-xs">{cluster.description}</span>
                    </div>
                  </div>

                  <span className="hidden text-right md:block">
                    <ValueText value={formatApy(cluster.highestApy)} />
                  </span>

                  <span className="hidden text-right md:block">
                    <ValueText value={formatApy(cluster.highestApy30d)} />
                  </span>

                  <span className="hidden text-right md:block">
                    <ValueText value={formatUsdCompact(cluster.totalTVL)} />
                  </span>

                  <div className="hidden md:flex md:justify-end">
                    {cluster.opportunities.length > 0 ? (
                      <div className="flex flex-col items-end gap-0.5">
                        <span className="font-medium text-foreground text-sm">{exposureSummary.assets}</span>
                        <span className="text-muted-foreground text-xs">{exposureSummary.pairs}</span>
                      </div>
                    ) : (
                      <Dash />
                    )}
                  </div>

                  <div className="hidden md:block" />
                </div>

                {isSelected && (
                  <div
                    className="mt-4 border-white/[0.06] border-t pt-4"
                    style={{
                      animationName: 'sweep-card-in',
                      animationDuration: '0.32s',
                      animationTimingFunction: 'ease-out',
                      animationFillMode: 'both',
                    }}
                  >
                    <div className="mb-3 grid grid-cols-2 gap-3 md:hidden sm:grid-cols-4">
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[10px] text-muted-foreground">APY</span>
                        <ValueText value={formatApy(cluster.highestApy)} />
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[10px] text-muted-foreground">30d APY</span>
                        <ValueText value={formatApy(cluster.highestApy30d)} />
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[10px] text-muted-foreground">TVL</span>
                        <ValueText value={formatUsdCompact(cluster.totalTVL)} />
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[10px] text-muted-foreground">Assets</span>
                        <span className="font-medium text-foreground text-sm">
                          {cluster.opportunities.length > 0 ? exposureSummary.assets : '-'}
                        </span>
                      </div>
                    </div>

                    <p className="max-w-xl text-muted-foreground text-xs leading-relaxed sm:text-sm">{style.details}</p>
                  </div>
                )}
              </div>
            </button>

            {isSelected && cluster.opportunities.length > 0 && (
              <div
                style={{
                  animationName: 'sweep-card-in',
                  animationDuration: '0.32s',
                  animationTimingFunction: 'ease-out',
                  animationFillMode: 'both',
                  transformOrigin: 'top',
                  animationDelay: '0.2s',
                }}
              >
                <ClusterDetailTable
                  clusterKey={cluster.key}
                  opportunities={cluster.opportunities}
                  clusterName={cluster.name}
                  elendExposureSummaries={elendExposureSummaries}
                />
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export function PlaceholderRow() {
  return (
    <div className="flex w-full items-center gap-5 rounded-lg border border-border border-dashed px-6 py-6 shadow-sm backdrop-blur-sm">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-white/[0.04]">
        <div className="h-5 w-5 rounded-md bg-white/10" />
      </div>
      <span className="text-muted-foreground/40 text-sm">More markets coming soon</span>
    </div>
  )
}

interface ClusterDetailTableProps {
  clusterKey: ClusterKey
  opportunities: YieldOpportunity[]
  clusterName: string
  elendExposureSummaries: Map<string, ELendVaultExposureSummary>
}

type DetailSortField = 'liveApy' | 'averageApy' | 'tvl' | 'holdings'
type SortDirection = 'asc' | 'desc'

interface DetailHeader {
  label: string
  className?: string
  tooltip?: string
  sortField?: DetailSortField
}

const DETAIL_COL_GRID = 'grid-cols-[minmax(0,1.2fr)_repeat(4,minmax(0,0.7fr))_minmax(100px,0.55fr)]'

function getDetailHeaders(clusterName: string): ReadonlyArray<DetailHeader> {
  return [
    { label: 'Asset', className: 'pl-8', tooltip: `Lending assets in the ${clusterName} market.` },
    {
      label: 'APY',
      tooltip: 'Current total supply APY, including lending APY and any intrinsic asset APY.',
      sortField: 'liveApy',
    },
    {
      label: '30d APY',
      tooltip: '30-day average lending APY. Intrinsic asset APY is not tracked in this average.',
      sortField: 'averageApy',
    },
    { label: 'TVL', tooltip: 'Total value currently deposited in this vault.', sortField: 'tvl' },
    {
      label: 'My Holdings',
      tooltip: 'Total USD value of your deposits and wallet balance for this asset. Hover for a breakdown.',
      sortField: 'holdings',
    },
    { label: '' },
  ]
}

function SortIcon({
  field,
  activeField,
  direction,
}: { field: DetailSortField; activeField: DetailSortField | null; direction: SortDirection }) {
  return (
    <div className="-space-y-1 flex flex-col">
      <ChevronUp
        className={cn(
          'h-3 w-3',
          activeField === field && direction === 'asc' ? 'text-foreground' : 'text-muted-foreground/40',
        )}
      />
      <ChevronDown
        className={cn(
          'h-3 w-3',
          activeField === field && direction === 'desc' ? 'text-foreground' : 'text-muted-foreground/40',
        )}
      />
    </div>
  )
}

function StaticCell({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn('flex items-center py-5', className)}>{children}</div>
}

function getApyBreakdown(opportunity: YieldOpportunity) {
  if (opportunity.sourceData.type === 'elend' || opportunity.sourceData.type === 'pooled') {
    return {
      lendingApy: opportunity.sourceData.lendingAPY ?? opportunity.supplyAPY,
      intrinsicApy: opportunity.sourceData.intrinsicAPY,
      totalApy: opportunity.supplyAPY,
    }
  }

  return {
    lendingApy: opportunity.supplyAPY,
    intrinsicApy: undefined,
    totalApy: opportunity.supplyAPY,
  }
}

function isCollateralOnlyOpportunity(
  opportunity: YieldOpportunity,
  exposureSummary?: ELendVaultExposureSummary,
): boolean {
  if (opportunity.sourceData.type === 'elend') {
    if (exposureSummary) {
      return exposureSummary.borrowableCount === 0 && exposureSummary.collateralCount > 0
    }
    return opportunity.sourceData.isCollateralOnly === true
  }

  if (opportunity.sourceData.type !== 'pooled') return false
  return opportunity.sourceData.isCollateralOnly === true
}

function CollateralOnlyApyInfo() {
  return (
    <Info size={14} className="cursor-help">
      This asset is collateral only and does not earn borrowing-driven APR.
    </Info>
  )
}

export function ClusterDetailTable({
  clusterKey,
  opportunities,
  clusterName,
  elendExposureSummaries,
}: ClusterDetailTableProps) {
  const [sortField, setSortField] = useState<DetailSortField | null>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const portfolio = usePortfolioEvmBalances()

  const sortedOpportunities = useMemo(() => {
    if (!sortField) {
      return [...opportunities].sort((a, b) => {
        const aRank = getMarketVaultSortRank(clusterKey, [a.address, a.token?.address, a.tokenInfo?.address])
        const bRank = getMarketVaultSortRank(clusterKey, [b.address, b.token?.address, b.tokenInfo?.address])
        const rankDiff = aRank - bRank
        return rankDiff !== 0 ? rankDiff : 0
      })
    }

    return [...opportunities].sort((a, b) => {
      let aVal: number
      let bVal: number

      if (sortField === 'liveApy') {
        aVal = a.supplyAPY ?? 0
        bVal = b.supplyAPY ?? 0
      } else if (sortField === 'averageApy') {
        aVal = a.supplyApy30d ?? 0
        bVal = b.supplyApy30d ?? 0
      } else if (sortField === 'holdings') {
        aVal = getPortfolioHoldingsSortValue(a, portfolio)
        bVal = getPortfolioHoldingsSortValue(b, portfolio)
      } else {
        aVal = a.tvlUsd?.toNumber() ?? 0
        bVal = b.tvlUsd?.toNumber() ?? 0
      }

      return sortDirection === 'desc' ? bVal - aVal : aVal - bVal
    })
  }, [clusterKey, opportunities, portfolio, sortField, sortDirection])

  const detailHeaders = getDetailHeaders(clusterName)

  const handleSort = (field: DetailSortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'desc' ? 'asc' : 'desc'))
    } else {
      setSortField(field)
      setSortDirection('desc')
    }
  }

  if (opportunities.length === 0) {
    return (
      <div className="flex items-center justify-center rounded-2xl border border-white/[0.06] bg-[#1a1a1e] px-8 py-16">
        <span className="text-muted-foreground text-sm">No markets in {clusterName}</span>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3 md:gap-0 md:overflow-hidden md:rounded-2xl md:border md:border-border md:bg-panel">
      <div className={cn('hidden items-center border-border border-b px-5 pt-5 pb-2 md:grid', DETAIL_COL_GRID)}>
        {detailHeaders.map(({ label, tooltip, sortField: sf }, index) => (
          <div
            key={label || `col-${index}`}
            className={cn('font-normal text-muted-foreground text-xs', index === 0 ? 'text-left' : 'flex justify-end')}
          >
            {label && tooltip ? (
              <span className="flex items-center gap-1">
                {sf ? (
                  <button
                    type="button"
                    onClick={() => handleSort(sf)}
                    className={cn(
                      'flex cursor-pointer items-center gap-1 transition-colors hover:text-foreground',
                      sortField === sf && 'text-foreground',
                    )}
                  >
                    {label}
                    <SortIcon field={sf} activeField={sortField} direction={sortDirection} />
                  </button>
                ) : (
                  label
                )}
                <Info size={12} className="text-muted-foreground">
                  {tooltip}
                </Info>
              </span>
            ) : (
              label
            )}
          </div>
        ))}
      </div>

      {sortedOpportunities.map((opportunity) => (
        <OpportunityRow
          key={opportunity.id}
          clusterKey={clusterKey}
          clusterName={clusterName}
          opportunity={opportunity}
          holdingsData={getPortfolioHoldingsData(opportunity, portfolio)}
          exposureSummary={elendExposureSummaries.get(opportunity.address.toLowerCase())}
        />
      ))}
    </div>
  )
}

function displayVaultTitle(opportunity: YieldOpportunity): string {
  if (opportunity.source === 'elend') {
    return cleanVaultName(opportunity.name)
  }
  return opportunity.name
}

function OpportunityRow({
  clusterKey,
  clusterName,
  opportunity,
  holdingsData,
  exposureSummary,
}: {
  clusterKey: ClusterKey
  clusterName: string
  opportunity: YieldOpportunity
  holdingsData: HoldingsData
  exposureSummary?: ELendVaultExposureSummary
}) {
  const address = useAccount()
  const openDialog = useOpenDialog()
  const router = useRouter()
  const assetSymbol = opportunity.assetSymbol || opportunity.symbol
  const displaySymbol = getDisplayTokenSymbol(opportunity.chainId, TokenSymbol(assetSymbol))
  const tokenImage = getTokenImageUrl(opportunity.tokenInfo, assetSymbol)
  const tokenAddress = opportunity.token?.address ?? opportunity.tokenInfo?.address
  const getAction = getMarketGetAction(clusterKey, [
    opportunity.address,
    opportunity.token?.address,
    opportunity.tokenInfo?.address,
  ])
  const canDepositFromRow =
    !!address &&
    (opportunity.source === 'evault' ||
      opportunity.source === 'elend' ||
      (opportunity.source === 'pooled' && !!opportunity.token))
  const href = getYieldOpportunityDetailsPath(opportunity)

  const navButton = (
    <Link
      href={href}
      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-opacity hover:opacity-80"
      aria-label={`Open ${displayVaultTitle(opportunity)}`}
    >
      <ArrowRight className="h-4 w-4" />
    </Link>
  )
  const apyBreakdown = getApyBreakdown(opportunity)
  const liveApyDisplay = formatApy(opportunity.supplyAPY)
  const isCollateralOnly = isCollateralOnlyOpportunity(opportunity, exposureSummary)
  const apyContent = (
    <span className="inline-flex items-center gap-1">
      <ValueText value={liveApyDisplay} />
    </span>
  )
  const liveApyContent = isCollateralOnly && opportunity.supplyAPY <= 0 ? <CollateralOnlyApyInfo /> : apyContent

  const stopRowNav = (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const shouldIgnoreRowNav = (target: EventTarget | null) =>
    target instanceof Element &&
    !!target.closest('a, button, input, select, textarea, [role="button"], [data-stop-row-nav]')

  const openDetails = () => {
    router.push(href)
  }

  const handleRowClick = (e: MouseEvent<HTMLDivElement>) => {
    if (shouldIgnoreRowNav(e.target)) return
    openDetails()
  }

  const handleRowKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter') return
    e.preventDefault()
    openDetails()
  }

  const openGetAction = (e: MouseEvent) => {
    stopRowNav(e)
    if (!getAction) return

    openDialog(marketGetActionDialogConfig, {
      action: getAction,
      assetSymbol: displaySymbol as string,
      marketName: clusterName,
    })
  }

  const openDepositAction = (e: MouseEvent) => {
    stopRowNav(e)
    if (!canDepositFromRow) return

    if (opportunity.source === 'pooled' && opportunity.token) {
      openDialog(pooledDepositDialogConfig, { token: opportunity.token })
      return
    }

    if (opportunity.source === 'evault' || opportunity.source === 'elend') {
      openDialog(marketDepositActionDialogConfig, {
        vaultAddress: opportunity.address,
        chainId: opportunity.chainId,
        source: opportunity.source,
      })
    }
  }

  const depositButton = canDepositFromRow ? (
    <Button variant="primary" size="sm" className="h-7 rounded-full px-3 text-xs" onClick={openDepositAction}>
      Deposit
    </Button>
  ) : null

  const renderGetButton = () =>
    getAction ? (
      <Button
        variant="yellow"
        size="sm"
        className="h-5 rounded-full px-1.5 font-bold text-[11px]"
        onClick={openGetAction}
        prefixIcon={<Sparkles className="h-2.5 w-2.5" />}
      >
        {getAction.label}
      </Button>
    ) : null

  return (
    <>
      {/* Desktop */}
      <div
        className={cn(
          'group hidden cursor-pointer items-center px-5 text-inherit no-underline outline-none transition-colors md:grid focus-visible:bg-white/[0.03] hover:bg-white/[0.02] focus-visible:ring-1 focus-visible:ring-primary/60 focus-visible:ring-inset',
          DETAIL_COL_GRID,
        )}
        role="link"
        tabIndex={0}
        aria-label={`Open ${displayVaultTitle(opportunity)}`}
        onClick={handleRowClick}
        onKeyDown={handleRowKeyDown}
      >
        <StaticCell>
          <div className="flex items-center gap-4">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-neutral-800">
              <TokenImage
                src={tokenImage}
                alt={assetSymbol}
                width={32}
                height={32}
                className="h-8 w-8 rounded-xl object-cover"
                tokenAddress={tokenAddress}
              />
            </div>
            <div className="flex flex-col gap-0.5">
              <div className="flex min-w-0 items-center gap-1.5">
                <span className="truncate font-semibold text-foreground text-sm leading-tight">{displaySymbol}</span>
                {opportunity.reserveStatus === 'frozen' && (
                  <span className="flex shrink-0">
                    <FrozenPill />
                  </span>
                )}
                {opportunity.fraxlendPauseState && hasAnyFraxlendPause(opportunity.fraxlendPauseState) && (
                  <span className="flex shrink-0">
                    <FraxlendPauseIndicator state={opportunity.fraxlendPauseState} />
                  </span>
                )}
                {renderGetButton()}
              </div>
              <span className="text-dim-foreground text-xs leading-tight">{getMarketType(opportunity)}</span>
            </div>
          </div>
        </StaticCell>
        <StaticCell className="justify-end">
          {opportunity.supplyAPY > 0 ? (
            <SupplyApyTooltip
              breakdown={apyBreakdown}
              collateralOnly={isCollateralOnly}
              marketType={opportunity.source}
              showPointsSection={false}
              vaultAddress={opportunity.address}
            >
              {apyContent}
            </SupplyApyTooltip>
          ) : (
            liveApyContent
          )}
        </StaticCell>
        <StaticCell className="justify-end">
          <ValueText value={formatApy(opportunity.supplyApy30d)} />
        </StaticCell>
        <StaticCell className="justify-end">
          <ValueText value={formatUsdCompact(opportunity.tvlUsd?.toNumber())} />
        </StaticCell>
        <StaticCell className="justify-end">
          <HoldingsCellBase {...holdingsData} />
        </StaticCell>
        <StaticCell className="justify-end">{depositButton}</StaticCell>
      </div>

      {/* Mobile */}
      <div className="flex flex-col gap-2 rounded-xl border border-white/[0.08] bg-white/[0.02] px-4 py-4 md:hidden">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-neutral-800">
              <TokenImage
                src={tokenImage}
                alt={assetSymbol}
                width={32}
                height={32}
                className="h-8 w-8 rounded-xl object-cover"
                tokenAddress={tokenAddress}
              />
            </div>
            <div className="flex flex-col gap-0.5">
              <div className="flex min-w-0 items-center gap-1.5">
                <span className="truncate font-semibold text-foreground text-sm leading-tight">{displaySymbol}</span>
                {opportunity.reserveStatus === 'frozen' && (
                  <span onClick={stopRowNav} className="flex shrink-0">
                    <FrozenPill />
                  </span>
                )}
                {opportunity.fraxlendPauseState && hasAnyFraxlendPause(opportunity.fraxlendPauseState) && (
                  <span onClick={stopRowNav} className="flex shrink-0">
                    <FraxlendPauseIndicator state={opportunity.fraxlendPauseState} />
                  </span>
                )}
                {renderGetButton()}
              </div>
              <span className="text-dim-foreground text-xs leading-tight">{getMarketType(opportunity)}</span>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">{navButton}</div>
        </div>

        <div className="mt-1 flex flex-col gap-2 border-white/[0.04] border-t pt-3">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-xs">APY</span>
            {opportunity.supplyAPY > 0 ? (
              <SupplyApyTooltip
                breakdown={apyBreakdown}
                collateralOnly={isCollateralOnly}
                marketType={opportunity.source}
                showPointsSection={false}
                vaultAddress={opportunity.address}
              >
                {apyContent}
              </SupplyApyTooltip>
            ) : (
              liveApyContent
            )}
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-xs">30d APY</span>
            <ValueText value={formatApy(opportunity.supplyApy30d)} />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-xs">TVL</span>
            <ValueText value={formatUsdCompact(opportunity.tvlUsd?.toNumber())} />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-xs">My Holdings</span>
            <HoldingsCellBase {...holdingsData} />
          </div>
          {depositButton && (
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground text-xs">Action</span>
              {depositButton}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
